import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'

import { useBillingStore } from '@/features/billing/store'
import type {
  EstimatePlanChange,
  EstimatePlanChangeInput,
} from '@/shared/graphql/router/graphql'

const ESTIMATE_PLAN_CHANGE = gql`
  query EstimatePlanChange($input: EstimatePlanChangeInput!) {
    estimatePlanChange(input: $input) {
      subscriptionLine {
        productId
        name
        amount {
          amount
          currency
          formatted
        }
      }
      fileLifetimeLine {
        quantity
        unitAmount {
          amount
          currency
          formatted
        }
        amount {
          amount
          currency
          formatted
        }
      }
      subtotal {
        amount
        currency
        formatted
      }
      totalTax {
        amount
        currency
        formatted
      }
      total {
        amount
        currency
        formatted
      }
    }
  }
`

export type PlanChangeEstimate = EstimatePlanChange

interface EstimatePlanChangeData {
  estimatePlanChange?: EstimatePlanChange | null
}

interface EstimatePlanChangeVars {
  input: EstimatePlanChangeInput
}

export function useChangePlanEstimate() {
  const route = useRoute()
  const billingStore = useBillingStore()

  const productId = computed(() => billingStore.selectedPlanItem?.productId)
  const enabled = computed(
    () => route.path.includes('change') && !!productId.value
  )

  const settled = ref(false)

  const { result, loading, onResult, onError } = useQuery<
    EstimatePlanChangeData,
    EstimatePlanChangeVars
  >(
    ESTIMATE_PLAN_CHANGE,
    () => ({ input: { productId: productId.value ?? '' } }),
    () => ({ clientId: 'router', enabled: enabled.value })
  )

  onResult(() => (settled.value = true))
  onError(() => (settled.value = true))

  const estimate = computed(() => result.value?.estimatePlanChange ?? null)
  const awaiting = computed(() => enabled.value && !settled.value)

  return { estimate, loading, awaiting }
}
